import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Actions } from '@ngrx/effects';
import { TranslocoService } from '@jsverse/transloco';

import {
  combineLatest,
  distinctUntilChanged,
  tap,
  withLatestFrom
} from 'rxjs';

import {
  TrackerImage,
  TrackerImageBatchData
} from '../../../../models/tracker';

import { Site } from '../../../../models/site';
import { User } from '../../../../models/user';
import { Batch } from '../../../../models/batch';
import { Attachment } from '../../../../models/attachment';

import { selectVolume } from '../../../../features/volume/volume.actions';
import { selectDetection } from '../../../../features/detection/detection.actions';
import { selectCut } from '../../../../features/cut/cut.actions';
import { selectMeasure } from '../../../../features/measure/measure.actions';

import {
  setCameraConfiguration,
  setCameraMode,
  setMapHandleKeyboard
} from '../../../../features/cesium/cesium.actions';

import { CameraMode } from '../../../../enums/camera';
import { CesiumTools } from '../../../../tools/cesium.tools';

import { DeleteConfirmDialog } from '../../../dialogs/delete-confirm-dialog/delete-confirm.dialog';
import { RenameDialog } from '../../../dialogs/rename-dialog/rename.dialog';

import { selectCurrentSite } from '../../../../features/site/site.selectors';
import { selectUser } from '../../../../features/user/user.selectors';

import {
  selectCurrentTrackerImage, selectMarkerShowById
} from '../../../../features/tracker/tracker.selectors';

import {
  deleteTracker,
  selectTrackerElevation,
  selectTrackerImage,
  setTrackerShow,
  updateTracker
} from '../../../../features/tracker/tracker.actions';

import {
  selectCurrentReferenceBatch
} from '../../../../features/batch/batch.selectors';

import {
  selectConfigDisplayMarkers,
  selectConfigItemDetailExpanded
} from '../../../../features/config/config.selectors';

import {
  setConfigExpandItemDetail
} from '../../../../features/config/config.actions';

import {
  selectCurrentOrganizationId
} from '../../../../features/organization/organization.selectors';

export enum SplitScreenMode {
  LEFT,
  SPLITTED,
  RIGHT
}

@Component({
    selector: 'marker-details',
    templateUrl: './marker-details.component.html',
    styleUrls: ['../details.component.scss', './marker-details.component.scss'],
    standalone: false
})
export class MarkerDetailsComponent {
  site: Site | null | undefined;
  marker: TrackerImage | null | undefined;
  referenceBatch: Batch | null | undefined;

  selectedSplitScreenMode: SplitScreenMode = SplitScreenMode.SPLITTED;

  lastAttachments: Attachment[] = [];
  referenceAttachments: Attachment[] = [];

  readonly displayMarkers$ = this.store.select(selectConfigDisplayMarkers);
  readonly isExpanded$ = this.store.select(selectConfigItemDetailExpanded);
  readonly currentUser$ = this.store.select(selectUser);
  readonly currentOrganizationId$ = this.store.select(selectCurrentOrganizationId);

  constructor(
    private readonly store: Store,
    private readonly actions: Actions,
    private readonly dialog: MatDialog,
    private readonly translocoService: TranslocoService
  ) {
    this.handleMarkerPictures();
  }

  private handleMarkerPictures(): void {
    combineLatest([
      this.store.select(selectCurrentTrackerImage),
      this.store.select(selectCurrentReferenceBatch),
    ])
      .pipe(
        withLatestFrom(
          this.store.select(selectCurrentSite),
        ),
        takeUntilDestroyed(),

        distinctUntilChanged(([
                                [markerA, refA],
                                siteA
                              ], [
                                [markerB, refB],
                                siteB
                              ]) =>
          (markerA?.id ?? null) === (markerB?.id ?? null) &&
          (refA?.id ?? null) === (refB?.id ?? null) &&
          (siteA?.id ?? null) === (siteB?.id ?? null)
        ),

        tap(([
               [marker, referenceBatch],
               site
             ]) => {
          if (!marker) {
            this.marker = undefined;
            this.lastAttachments = [];
            this.referenceAttachments = [];
            return;
          }

          this.site = site ?? undefined;
          this.marker = marker ?? undefined;
          this.referenceBatch = referenceBatch ?? undefined;

          this.store.dispatch(selectMeasure({ id: undefined }));
          this.store.dispatch(selectVolume({ id: undefined }));
          this.store.dispatch(selectCut({ id: undefined }));
          this.store.dispatch(selectDetection({ id: undefined }));
          this.store.dispatch(selectTrackerElevation({ id: undefined }));

          const trackerImageLastBatchData = marker.data.filter(
            (data: TrackerImageBatchData) => data.batch === this.site?.lastBatch
          );

          this.lastAttachments =
            trackerImageLastBatchData.length > 0
              ? (trackerImageLastBatchData[0].attachments || [])
              : [];

          if (referenceBatch) {
            const trackerImageReferenceBatchData = marker.data.filter(
              (data: TrackerImageBatchData) => data.batch === referenceBatch.id
            );

            this.referenceAttachments =
              trackerImageReferenceBatchData.length > 0
                ? (trackerImageReferenceBatchData[0].attachments || [])
                : [];
          } else {
            this.referenceAttachments = [];
          }
        })
      )
      .subscribe();
  }

  onHandleFocus(): void {
    this.store.dispatch(setMapHandleKeyboard({ mapHandleKeyboard: false }));
  }

  onFocusLeave(): void {
    this.store.dispatch(setMapHandleKeyboard({ mapHandleKeyboard: true }));
  }

  onClickFlyTo(marker: TrackerImage): void {
    const cameraConfiguration = CesiumTools.cameraConfigurationFromTargetAndDirection(
      marker.center,
      marker.cameraDirection,
      marker.length
    );

    if (!cameraConfiguration) {
      return;
    }

    this.store.dispatch(setCameraMode({ cameraMode: CameraMode.MODE_3D }));
    this.store.dispatch(setCameraConfiguration({ cameraConfiguration }));
  }

  onClickClose(): void {
    this.store.dispatch(setConfigExpandItemDetail({ expand: false }));
    this.store.dispatch(selectTrackerImage({ id: undefined }));
  }

  onClickExpand(): void {
    this.store.dispatch(setConfigExpandItemDetail({ expand: true }));
  }

  onClickCollapse(): void {
    this.store.dispatch(setConfigExpandItemDetail({ expand: false }));
  }

  onClickDelete(marker: TrackerImage): void {
    const deleteDialogRef = this.dialog.open(DeleteConfirmDialog, {
      disableClose: false
    });

    deleteDialogRef.componentInstance.message = this.translocoService.translate(
      'would_you_like_to_delete',
      { name: marker.name }
    );

    deleteDialogRef.afterClosed().subscribe((result) => {
      if (result && this.site) {
        this.store.dispatch(selectTrackerImage({ id: undefined }));

        this.store.dispatch(deleteTracker({
          organizationId: this.site.organization,
          siteId: this.site.id,
          trackerId: marker.id
        }));
      }
    });
  }

  onClickRename(marker: TrackerImage): void {
    this.store.dispatch(setMapHandleKeyboard({ mapHandleKeyboard: false }));

    const renameDialogRef = this.dialog.open(RenameDialog, {
      disableClose: false
    });

    renameDialogRef.componentInstance.placeholder = marker.name;

    renameDialogRef.afterClosed().subscribe((result) => {
      this.store.dispatch(setMapHandleKeyboard({ mapHandleKeyboard: true }));

      if (result && this.marker && this.site) {
        this.store.dispatch(updateTracker({
          organizationId: this.site.organization,
          siteId: this.site.id,
          trackerId: marker.id,
          payload: {
            name: result,
            is_shared: marker.isShared
          }
        }));
      }
    });
  }

  onClickToggleShare(marker: TrackerImage): void {
    if (!this.site) {
      return;
    }

    this.store.dispatch(updateTracker({
      organizationId: this.site.organization,
      siteId: this.site.id,
      trackerId: marker.id,
      payload: {
        name: marker.name,
        is_shared: !marker.isShared
      }
    }));
  }

  onClickToggleShow(marker: TrackerImage): void {
    this.store.dispatch(setTrackerShow({
      id: marker.id,
      show: !marker.show
    }));
  }

  onSplitModeChanged(mode: SplitScreenMode): void {
    this.selectedSplitScreenMode = Number(mode);
  }

  dateFormat(user: User): string {
    return user.languageCode === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
  }
}
